import { ApolloClient, ApolloProvider, from, InMemoryCache, Observable } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";
import Cookie from "js-cookie";
import { useContext, useEffect, useMemo } from "react";

import { IsAuthenticatedContext } from "@/auth";
import { MONETA_API_BASE_URL, MONETA_GRAPHQL_URL } from "@/config";

let csrfInitialized = false;
const getCsrfCookie = () => Cookie.get("XSRF-TOKEN") || Cookie.get("xsrf-token") || "";

const fetchCsrfToken = async () => {
  try {
    const response = await fetch(`${MONETA_API_BASE_URL}/csrf/token`, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `CSRF token fetch failed with status ${response.status}: ${JSON.stringify(errorData)}`
      );
    }

    const data = await response.json();

    if (!data?.token) {
      throw new Error("CSRF token missing in response");
    }

    return data;
  } catch (error) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-console
    console.error("CSRF token fetch error:", {
      error: error instanceof Error ? error.message : "Unknown error",
      url: "/csrf/token",
      time: new Date().toISOString(),
    });
    throw error;
  }
};

const initializeCsrf = async () => {
  if (!csrfInitialized) {
    try {
      csrfInitialized = true;
      await fetchCsrfToken();
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Initial CSRF token fetch failed, retrying...");

      await new Promise((resolve) => setTimeout(resolve, 1000));
      try {
        await fetchCsrfToken();
      } catch (retryError) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-console
        console.error("CSRF token initialization failed after retry:", retryError);
      }
    }
  }
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let apolloClient: ApolloClient<any>;

const createApolloClient = () => {
  const dynamicCsrfLink = setContext(async (_, { headers }) => {
    try {
      let csrfToken = getCsrfCookie();

      if (!csrfToken) {
        const tokenData = await fetchCsrfToken().catch(() => null);
        csrfToken = tokenData?.token || "";
      }

      return {
        headers: {
          ...headers,
          ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
        },
      };
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.warn("CSRF token context setup failed, proceeding without token");
      return { headers };
    }
  });

  const httpLink = createUploadLink({
    uri: MONETA_GRAPHQL_URL,
    credentials: "include",
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err.message.includes("CSRF") || err.extensions?.code === "FORBIDDEN") {
          return new Observable((observer) => {
            fetchCsrfToken()
              .then(() => {
                operation.setContext({
                  headers: {
                    ...operation.getContext().headers,
                    "x-csrf-token": getCsrfCookie(),
                  },
                });
                forward(operation).subscribe(observer);
              })
              .catch(observer.error.bind(observer));
          });
        }

        const ext = err.extensions ?? {};
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const code = ext.code ?? (err as any).code;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const scode = ext.statusCode ?? (err as any).statusCode;

        if (code === "UNAUTHENTICATED" || scode === 401 || scode === 403) {
          const isLoggedIn = localStorage.getItem("isLoggedIn");
          if (isLoggedIn === "true" && !window.location.pathname.includes("/login")) {
            localStorage.removeItem("isLoggedIn");
            localStorage.removeItem("username");
            localStorage.setItem("logout", Date.now().toString());
            window.location.href = "/login";
          }
        }
      }
    }

    if (
      networkError &&
      "statusCode" in networkError &&
      (networkError.statusCode === 401 || networkError.statusCode === 403)
    ) {
      const isLoggedIn = localStorage.getItem("isLoggedIn");

      if (isLoggedIn === "true" && !window.location.pathname.includes("/login")) {
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("username");
        localStorage.setItem("logout", Date.now().toString());
        window.location.href = "/login";
      }
    }
  });

  return new ApolloClient({
    link: from([errorLink, dynamicCsrfLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      },
      query: {
        fetchPolicy: "network-only",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  });
};

export const getApolloClient = () => {
  if (!apolloClient) {
    apolloClient = createApolloClient();
  }
  return apolloClient;
};

export const ApolloClientProvider: React.FC = ({ children }) => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isAuthenticated] = useContext(IsAuthenticatedContext);

  useEffect(() => {
    initializeCsrf();
  }, []);

  const client = useMemo(() => getApolloClient(), []);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
